// Formatting + param helpers for the Workshop Analytics page. Every number is computed
// server-side (apps/api/analytics.py) — nothing here aggregates rows.
import { format } from "date-fns";
import { formatWaited } from "./dashboardTokens";

// `label` doubles as the dates.js granularity name, so defaultRangeFor(label) works.
export const BUCKETS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

export function toRangeParams(range) {
  if (!range?.from || !range?.to) return null;
  return { from: format(range.from, "yyyy-MM-dd"), to: format(range.to, "yyyy-MM-dd") };
}

// Hours as words ("1 day 2 hours"). Null means nothing finished to measure.
export function formatHours(hours) {
  if (hours == null || Number.isNaN(Number(hours))) return "—";
  const mins = Number(hours) * 60;
  if (mins < 1) return "Under a minute";
  return formatWaited(mins);
}

export function formatPct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return "—";
  return `${Number(pct).toFixed(1)}%`;
}

export function formatRatio(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(1);
}

// bucket_start is a yyyy-MM-dd date in tenant time; parse as a local date, not UTC.
export function bucketTickLabel(isoDate, bucket) {
  const d = new Date(`${isoDate}T00:00:00`);
  return bucket === "month" ? format(d, "MMM yyyy") : format(d, "MMM d");
}

// Whole-percent change vs the previous period; null when there is nothing to compare to.
export function volumeDelta(total, previous) {
  if (!previous) return null;
  return Math.round(((total - previous) / previous) * 100);
}

export function pluralize(unit) {
  const u = String(unit || "");
  return u.endsWith("s") ? u : `${u}s`;
}

const EVENT_LABEL = {
  job_card_sent: "the job card / repair order was sent",
  pre_delivery_feedback_sent: "pre-delivery feedback was sent",
  post_delivery_feedback_sent: "post-delivery feedback was sent",
  vehicle_delivered: "the vehicle was delivered",
};

export function eventLabel(key) {
  return EVENT_LABEL[key] || String(key || "").replace(/_/g, " ");
}

// Every escalation type, in SLA editor order. All are shown as columns even
// at zero; anything new from the api still shows after them, title-cased.
// "Raised Issue Escalations" (ladder key 'escalations') used to be here --
// a customer tapping "Report an Issue" on post-delivery inspection, sourced
// from a separate manual-flag table with no ladder/rungs/config behind it,
// unlike every other entry below. The SQL no longer folds it in (see
// supabase/migrations/20260930110000_drop_manual_escalations_from_rate.sql),
// so it's dropped here too rather than left as a label nothing produces.
// 'sentiment' is the LLM's general escalation_flag (safety concerns,
// explicit anger, repeated unresolved issues) -- not the same thing as the
// separate, real `complaint` ladder (AI-detected complaint records, not yet
// its own column here). Labelled "Customer Complaints" anyway per product
// call; if `complaint` ever gets its own column, the two will read as the
// same thing and need disambiguating then.
const LADDER_LABEL = {
  response: "Pending Response Escalations",
  join: "Members Not Joined",
  sentiment: "Customer Complaints",
  predelivery_feedback: "Negative Pre-Delivery Feedback",
  postdelivery_feedback: "Negative Post-Delivery Feedback",
  staff_conduct: "Staff Behavior Issues",
};

export function ladderLabel(key) {
  if (LADDER_LABEL[key]) return LADDER_LABEL[key];
  const words = String(key || "").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Same identity the backend uses for the advisor dimension (last 10 digits of
// the number) — lets the frontend match a workshop_service_advisors roster
// entry to a dim_key the api returns.
export function advisorKey(number) {
  const digits = String(number || "").replace(/\D/g, "");
  return digits.slice(-10);
}

// Kept out of the Service Cases Escalated table's per-ladder columns even
// though the api still reports it: 'response' has its own dedicated panel
// (Unanswered Customer Messages, 2.3), so a column here would just repeat
// it. Still counts toward that table's own Service Groups / Percentage of
// escalation totals, and ladderLabel('response') still resolves correctly
// wherever an individual escalation names its ladder (e.g. EscalationDetail).
const HIDDEN_LADDER_COLUMNS = new Set(["response"]);

export function ladderKeys(rows) {
  const keys = new Set(Object.keys(LADDER_LABEL));
  (rows || []).forEach((r) => Object.keys(r.by_ladder || {}).forEach((k) => keys.add(k)));
  HIDDEN_LADDER_COLUMNS.forEach((k) => keys.delete(k));
  const order = Object.keys(LADDER_LABEL);
  const rank = (k) => (order.includes(k) ? order.indexOf(k) : order.length);
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}
